import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Lightbulb, TrendingUp, AlertCircle } from 'lucide-react';
import { OptimizationOpportunity } from '@/types/analytics';
import { formatCurrency } from '@/components/calculator/utils/formatters';
import { MetricTooltip } from './MetricTooltip';

interface OptimizationCardProps {
  opportunities: OptimizationOpportunity[];
}

export const OptimizationCard: React.FC<OptimizationCardProps> = ({ opportunities }) => {
  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'High': return 'bg-red-100 text-red-800 border-red-300 dark:bg-red-950/50 dark:text-red-200 dark:border-red-900';
      case 'Medium': return 'bg-yellow-100 text-yellow-800 border-yellow-300 dark:bg-yellow-950/50 dark:text-yellow-200 dark:border-yellow-900';
      case 'Low': return 'bg-blue-100 text-blue-800 border-blue-300 dark:bg-blue-950/50 dark:text-blue-200 dark:border-blue-900';
      default: return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };

  const getDifficultyIcon = (difficulty: string) => {
    switch (difficulty) {
      case 'Easy': return '✓';
      case 'Medium': return '◐';
      case 'Hard': return '⚠';
      default: return '·';
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
           <Lightbulb className="w-5 h-5" />
          Optimization Opportunities
          <MetricTooltip metric="optimizationOpportunities" />
        </CardTitle>
      </CardHeader>
      <CardContent>
        {opportunities.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <AlertCircle className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p>Your mortgage is well-optimized! No major opportunities found.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {opportunities.map(opp => (
              <div key={opp.id} className="border rounded-lg p-4 space-y-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1">
                    <h4 className="font-semibold text-base">{opp.title}</h4>
                    <p className="text-sm text-muted-foreground mt-1">{opp.description}</p>
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    <Badge className={getPriorityColor(opp.priority)}>
                      {opp.priority} Priority
                    </Badge>
                    <span className="text-xs text-muted-foreground">
                      {getDifficultyIcon(opp.implementationDifficulty)} {opp.implementationDifficulty}
                    </span>
                  </div>
                </div>

                {opp.potentialSavings > 0 && (
                  <div className="flex items-center gap-2 text-green-600">
                    <TrendingUp className="w-4 h-4" />
                    <span className="font-semibold">
                      Potential Savings: {formatCurrency(opp.potentialSavings)}
                    </span>
                  </div>
                )}

                <div className="bg-muted rounded-md p-3">
                  <p className="text-xs font-semibold mb-2">Action Items:</p>
                  <ul className="space-y-1">
                    {opp.actionItems.map((item, idx) => (
                      <li key={idx} className="text-xs flex items-start gap-2">
                        <span className="text-primary mt-0.5">•</span>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
